import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  alpha,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import CalculateIcon from '@mui/icons-material/Calculate'
import BusinessIcon from '@mui/icons-material/Business'
import {
  MetricCard,
  TrendChart,
  DateRangePicker,
  ReferrerBreakdown,
} from '../../components/Performance'
import {
  useAdminAnalytics,
  aggregateSummaries,
  splitSummariesForComparison,
  calculateTrend,
  DailySummary,
} from '../../hooks/useAnalytics'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'

interface TopPerformer {
  id: string
  name: string
  type: 'undertaker' | 'chain'
  logo_url: string | null
  profile_views: number
  phone_reveals: number
  email_reveals: number
}

export default function PerformanceOverview() {
  const { isAdmin, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [days, setDays] = useState(30)
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([])
  const [performersLoading, setPerformersLoading] = useState(true)

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/performance/profile')
    }
  }, [isAdmin, authLoading, navigate])

  const { data: summaries = [], isLoading } = useAdminAnalytics(days)
  const metrics = aggregateSummaries(summaries)
  const { current, previous } = splitSummariesForComparison(summaries, days)

  // Aggregate by undertaker to get top performers
  useEffect(() => {
    async function fetchTopPerformers() {
      setPerformersLoading(true)
      try {
        // Group summaries by undertaker_id
        const undertakerMetrics = new Map<string, {
          profile_views: number
          phone_reveals: number
          email_reveals: number
        }>()
        
        summaries.forEach((s) => {
          if (s.undertaker_id) {
            const existing = undertakerMetrics.get(s.undertaker_id) || {
              profile_views: 0,
              phone_reveals: 0,
              email_reveals: 0,
            }
            undertakerMetrics.set(s.undertaker_id, {
              profile_views: existing.profile_views + (s.profile_views || 0),
              phone_reveals: existing.phone_reveals + (s.phone_reveals || 0),
              email_reveals: existing.email_reveals + (s.email_reveals || 0),
            })
          }
        })
        
        // Sort by profile_views and take top 10
        const sortedIds = Array.from(undertakerMetrics.entries())
          .sort((a, b) => b[1].profile_views - a[1].profile_views)
          .slice(0, 10)
          .map(([id]) => id)
        
        if (sortedIds.length === 0) {
          setTopPerformers([])
          return
        }
        
        // Fetch undertaker details
        const { data: undertakers } = await supabase
          .from('undertakers')
          .select('id, name, logo_url')
          .in('id', sortedIds)
        
        if (undertakers) {
          const performers = sortedIds.map((id) => {
            const undertaker = undertakers.find((u) => u.id === id)
            const perfMetrics = undertakerMetrics.get(id)!
            return {
              id,
              name: undertaker?.name || 'Unknown',
              type: 'undertaker' as const,
              logo_url: undertaker?.logo_url || null,
              ...perfMetrics,
            }
          })
          setTopPerformers(performers)
        }
      } catch (error) {
        console.error('Error fetching top performers:', error)
      } finally {
        setPerformersLoading(false)
      }
    }
    
    if (summaries.length > 0) {
      fetchTopPerformers()
    } else {
      setPerformersLoading(false)
      setTopPerformers([])
    }
  }, [summaries])

  // Calculate totals
  const totalUndertakers = new Set(summaries.filter((s) => s.undertaker_id).map((s) => s.undertaker_id)).size
  const totalChains = new Set(summaries.filter((s) => s.chain_id).map((s) => s.chain_id)).size

  if (!isAdmin) {
    return null
  }

  return (
    <Box>
      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 3,
        }}
      >
        <DateRangePicker value={days} onChange={setDays} />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Overview Cards */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <MetricCard
                title="Samlede visninger"
                value={metrics.total_profile_views}
                trend={calculateTrend(current.total_profile_views, previous.total_profile_views)}
                trendLabel="vs. forrige"
                icon={<VisibilityIcon sx={{ fontSize: 20 }} />}
                color="primary"
              />
            </Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <MetricCard
                title="Telefon vist"
                value={metrics.total_phone_reveals}
                trend={calculateTrend(current.total_phone_reveals, previous.total_phone_reveals)}
                trendLabel="vs. forrige"
                icon={<PhoneIcon sx={{ fontSize: 20 }} />}
                color="success"
              />
            </Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <MetricCard
                title="Email vist"
                value={metrics.total_email_reveals}
                trend={calculateTrend(current.total_email_reveals, previous.total_email_reveals)}
                trendLabel="vs. forrige"
                icon={<EmailIcon sx={{ fontSize: 20 }} />}
                color="warning"
              />
            </Box>
            <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <MetricCard
                title="Prisberegner visninger"
                value={metrics.total_prisberegner_impressions}
                trend={calculateTrend(current.total_prisberegner_impressions, previous.total_prisberegner_impressions)}
                trendLabel="vs. forrige"
                icon={<CalculateIcon sx={{ fontSize: 20 }} />}
                color="info"
              />
            </Box>
            {/* prisberegner_leads card removed — aggregation never writes that
                field, so it always showed 0. Re-add when lead aggregation from
                prisberegner_offers exists. */}
            <Box sx={{ flex: '1 1 160px', minWidth: 160 }}>
              <MetricCard
                title="Aktive bedemænd"
                value={totalUndertakers}
                subtitle={`${totalChains} kæder`}
                icon={<BusinessIcon sx={{ fontSize: 20 }} />}
                color="primary"
              />
            </Box>
          </Box>

          {/* Charts */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Box sx={{ flex: '1 1 600px', minWidth: 300 }}>
              <Card>
                <CardContent>
                  <TrendChart
                    data={summaries.reduce((acc: DailySummary[], curr) => {
                      const existing = acc.find((a) => a.summary_date === curr.summary_date)
                      if (existing) {
                        existing.profile_views += curr.profile_views || 0
                        existing.phone_reveals += curr.phone_reveals || 0
                        existing.email_reveals += curr.email_reveals || 0
                      } else {
                        acc.push({ ...curr })
                      }
                      return acc
                    }, [])}
                    metrics={[
                      { key: 'profile_views', label: 'Visninger', color: '#5c4b99' },
                      { key: 'phone_reveals', label: 'Telefon vist', color: '#16a34a' },
                      { key: 'email_reveals', label: 'Email vist', color: '#d97706' },
                    ]}
                    title="Platform aktivitet"
                    height={350}
                  />
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <ReferrerBreakdown metrics={metrics} />
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Top Performers Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Top 10 bedemænd
              </Typography>
              {performersLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : topPerformers.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                  }}
                >
                  <Typography color="text.secondary">
                    Ingen data tilgængelig for den valgte periode
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width={50}>#</TableCell>
                        <TableCell>Bedemand</TableCell>
                        <TableCell align="right">Visninger</TableCell>
                        <TableCell align="right">Telefon vist</TableCell>
                        <TableCell align="right">Email vist</TableCell>
                        <TableCell align="right">Konvertering</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topPerformers.map((performer, index) => {
                        const conversion = performer.profile_views > 0
                          ? ((performer.phone_reveals + performer.email_reveals) / performer.profile_views) * 100
                          : 0
                        return (
                          <TableRow key={performer.id} hover>
                            <TableCell>
                              <Chip
                                label={index + 1}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: index < 3 
                                    ? alpha('#d97706', 0.1) 
                                    : 'background.default',
                                  color: index < 3 ? '#d97706' : 'text.secondary',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  src={performer.logo_url || undefined}
                                  sx={{ width: 36, height: 36 }}
                                >
                                  {performer.name.charAt(0)}
                                </Avatar>
                                <Typography fontWeight={500}>
                                  {performer.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>
                                {performer.profile_views.toLocaleString('da-DK')}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {performer.phone_reveals.toLocaleString('da-DK')}
                            </TableCell>
                            <TableCell align="right">
                              {performer.email_reveals.toLocaleString('da-DK')}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${conversion.toFixed(1)}%`}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  backgroundColor: conversion >= 10 
                                    ? alpha('#16a34a', 0.1)
                                    : conversion >= 5
                                    ? alpha('#d97706', 0.1)
                                    : alpha('#6b7280', 0.1),
                                  color: conversion >= 10 
                                    ? '#16a34a'
                                    : conversion >= 5
                                    ? '#d97706'
                                    : '#6b7280',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  )
}
